import { useId, useMemo, useState } from 'react';
import {
  addDays,
  createEvent,
  isAmbiguousLocal,
  resolveLocal,
  Temporal,
  updateEvent,
  type Alarm,
  type EventInput,
  type IconId,
  type ISODate,
  type LocalEvent,
  type OccurrenceKey,
  type RecurrenceRule,
  type Timing,
  type Weekday,
  commit,
  iso,
} from '@autoday/core';
import { getStore, useStore } from '../store/appStore.ts';
import { Button, Field, Sheet, inputCls } from '../ui/kit.tsx';
import { ICONS } from '../ui/icons.tsx';
import { errorText, toast } from '../ui/toast.tsx';
import { randomId } from '../platform.ts';
import { useLang, tr } from '../i18n/i18n.ts';
import { calendarName, categoryName, iconLabel, weekdayName } from '../i18n/format.ts';

export type EditorRequest = { mode: 'new'; date: ISODate; time?: string } | { mode: 'edit'; id: string; occurrenceKey?: OccurrenceKey };

const ALARM_PRESETS: Array<{ minutes: number; sv: string; en: string }> = [
  { minutes: -10080, sv: '1 vecka före', en: '1 week before' },
  { minutes: -1440, sv: '1 dag före', en: '1 day before' },
  { minutes: -120, sv: '2 timmar före', en: '2 hours before' },
  { minutes: -60, sv: '1 timme före', en: '1 hour before' },
  { minutes: -15, sv: '15 minuter före', en: '15 minutes before' },
  { minutes: 0, sv: 'Vid start', en: 'At start' },
];
const presetLabel = (p: (typeof ALARM_PRESETS)[number], allDay: boolean) =>
  allDay && p.minutes > -1440 ? tr(p.sv.replace('före', 'före dagens början'), p.minutes === 0 ? 'At the start of the day' : p.en.replace('before', 'before the day starts')) : tr(p.sv, p.en);

type RecurChoice = 'none' | 'daily' | 'weekly' | 'biweekly' | 'monthly' | 'yearly';

function recurFrom(r?: RecurrenceRule): RecurChoice {
  if (!r) return 'none';
  if (r.freq === 'DAILY') return 'daily';
  if (r.freq === 'WEEKLY') return r.interval === 2 ? 'biweekly' : 'weekly';
  return r.freq === 'MONTHLY' ? 'monthly' : 'yearly';
}

export function EventEditor({ request, onClose }: { request: EditorRequest; onClose: () => void }) {
  const { state } = useStore();
  const store = getStore();
  const { t } = useLang();
  const tz = state.settings.timeZone;
  const existing: LocalEvent | undefined = request.mode === 'edit' ? state.events[request.id] : undefined;
  const occKey = request.mode === 'edit' ? request.occurrenceKey : undefined;
  const ov = existing && occKey ? existing.overrides?.[occKey] : undefined;
  const baseTiming: Timing | undefined = ov?.timing ?? existing?.timing;
  const uid = useId();

  const initDate = baseTiming ? (baseTiming.kind === 'allDay' ? baseTiming.start : baseTiming.start.slice(0, 10)) : request.mode === 'new' ? request.date : '';
  const [title, setTitle] = useState(ov?.title ?? existing?.title ?? '');
  const [calendarId, setCalendarId] = useState(existing?.calendarId ?? 'cal-privat');
  const [categoryId, setCategoryId] = useState(existing?.categoryId ?? 'privat');
  const [icon, setIcon] = useState<IconId>(existing?.icon ?? 'home');
  const [allDay, setAllDay] = useState(baseTiming ? baseTiming.kind === 'allDay' : request.mode === 'new' && !request.time ? false : false);
  const [date, setDate] = useState(initDate);
  const [endDate, setEndDate] = useState(baseTiming?.kind === 'allDay' ? addDays(baseTiming.endExclusive, -1) : baseTiming?.kind === 'timed' ? baseTiming.end.slice(0, 10) : initDate);
  const [start, setStart] = useState(baseTiming?.kind === 'timed' ? baseTiming.start.slice(11, 16) : request.mode === 'new' && request.time ? request.time : '09:00');
  const [end, setEnd] = useState(baseTiming?.kind === 'timed' ? baseTiming.end.slice(11, 16) : request.mode === 'new' && request.time ? Temporal.PlainTime.from(request.time).add({ hours: 1 }).toString().slice(0, 5) : '10:00');
  const [policy, setPolicy] = useState<'wall' | 'elapsed'>(baseTiming?.kind === 'timed' ? baseTiming.durationPolicy : 'wall');
  const [timeZone] = useState(baseTiming?.kind === 'timed' ? baseTiming.timeZone : tz);
  const [location, setLocation] = useState(ov?.location ?? existing?.location ?? '');
  const [notes, setNotes] = useState(ov?.notes ?? existing?.notes ?? '');
  const [url, setUrl] = useState(existing?.url ?? '');
  const [priority, setPriority] = useState<'normal' | 'high'>(existing?.priority ?? 'normal');
  const [alarms, setAlarms] = useState<number[]>(() =>
    existing ? existing.alarms.filter((a): a is Extract<Alarm, { kind: 'relative' }> => a.kind === 'relative').map((a) => a.offsetMinutes) : state.settings.defaultAlarmsMinutes,
  );
  const [recur, setRecur] = useState<RecurChoice>(recurFrom(existing?.recurrence));
  const [weekdays, setWeekdays] = useState<Weekday[]>(existing?.recurrence?.byWeekday ?? [Temporal.PlainDate.from(initDate).dayOfWeek as Weekday]);
  const [endMode, setEndMode] = useState<'never' | 'until' | 'count'>(existing?.recurrence?.until ? 'until' : existing?.recurrence?.count ? 'count' : 'never');
  const [until, setUntil] = useState(existing?.recurrence?.until ?? addDays(initDate, 90));
  const [count, setCount] = useState(existing?.recurrence?.count ?? 10);
  const [scope, setScope] = useState<'occurrence' | 'series'>(occKey && existing?.recurrence ? 'occurrence' : 'series');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [opId] = useState(() => randomId('ed_'));

  const categories = Object.values(state.categories);
  const writable = Object.values(state.calendars).filter((c) => c.kind !== 'import');

  const timing: Timing | null = useMemo(() => {
    if (!date) return null;
    if (allDay) return { kind: 'allDay', start: date, endExclusive: addDays(endDate && endDate >= date ? endDate : date, 1) };
    const eDate = end <= start && endDate === date ? addDays(date, 1) : endDate || date;
    return { kind: 'timed', start: `${date}T${start}`, end: `${eDate}T${end}`, timeZone, durationPolicy: policy };
  }, [allDay, date, endDate, start, end, timeZone, policy]);

  const dstNote = useMemo((): { gap: boolean; text: string } | null => {
    if (!timing || timing.kind !== 'timed') return null;
    if (resolveLocal(timing.start, timing.timeZone, 'compatible').adjusted)
      return { gap: true, text: tr(`Klockslaget ${start} finns inte den dagen (sommartid börjar). Välj en annan tid.`, `The time ${start} does not exist that day (daylight saving starts). Choose another time.`) };
    if (isAmbiguousLocal(timing.start, timing.timeZone))
      return { gap: false, text: tr(`Klockslaget ${start} förekommer två gånger den dagen (sommartid slutar). AutoDay använder den första.`, `The time ${start} occurs twice that day (daylight saving ends). AutoDay uses the first one.`) };
    return null;
  }, [timing, start]);

  const recurrence: RecurrenceRule | undefined =
    recur === 'none'
      ? undefined
      : {
          freq: recur === 'daily' ? 'DAILY' : recur === 'weekly' || recur === 'biweekly' ? 'WEEKLY' : recur === 'monthly' ? 'MONTHLY' : 'YEARLY',
          interval: recur === 'biweekly' ? 2 : 1,
          ...(recur === 'weekly' || recur === 'biweekly' ? { byWeekday: weekdays } : {}),
          ...(endMode === 'until' ? { until } : endMode === 'count' ? { count } : {}),
        };

  const onCategory = (id: string) => {
    setCategoryId(id);
    const c = state.categories[id];
    if (c && (!existing || icon === state.categories[categoryId]?.icon)) setIcon(c.icon);
  };

  async function save() {
    if (saving || !timing) return;
    setError(null);
    setSaving(true);
    const input: EventInput = {
      calendarId,
      title,
      categoryId,
      icon,
      timing,
      location,
      notes,
      url: url || undefined,
      priority,
      alarms: [...new Set(alarms)].sort((a, b) => a - b).map((m, i) => ({ id: `a${i}`, kind: 'relative' as const, offsetMinutes: m, related: 'start' as const, action: 'DISPLAY' as const, origin: 'user' as const })),
      ...(recurrence ? { recurrence } : {}),
    };
    try {
      const now = store.clock.now();
      if (request.mode === 'new') {
        const r = await store.run((s) => createEvent(s, opId, input, now));
        toast({ kind: 'ok', text: t(`"${title.trim()}" sparad.`, `"${title.trim()}" saved.`), action: r.undo ? { label: t('Ångra', 'Undo'), run: () => void store.undo(r.undo!.opId) } : undefined });
      } else if (existing && occKey && existing.recurrence && scope === 'occurrence') {
        // Endast denna förekomst → override.
        const r = await store.run((s) => {
          const ev = s.events[existing.id]!;
          const overrides = { ...(ev.overrides ?? {}), [occKey]: { ...(ev.overrides?.[occKey] ?? {}), title: title.trim(), location, notes, timing } };
          return commit(s, opId, `Ändra en förekomst av "${ev.title}"`, { events: { [ev.id]: { ...ev, overrides, revision: ev.revision + 1, updatedAt: iso(now) } } }, now);
        });
        toast({ kind: 'ok', text: t('Förekomsten uppdaterad.', 'Occurrence updated.'), action: r.undo ? { label: t('Ångra', 'Undo'), run: () => void store.undo(r.undo!.opId) } : undefined });
      } else if (existing) {
        const r = await store.run((s) => updateEvent(s, opId, existing.id, input, now, existing.revision));
        toast({ kind: 'ok', text: t('Ändringen sparad.', 'Change saved.'), action: r.undo ? { label: t('Ångra', 'Undo'), run: () => void store.undo(r.undo!.opId) } : undefined });
      }
      onClose();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setSaving(false);
    }
  }

  const toggleAlarm = (m: number) => setAlarms((a) => (a.includes(m) ? a.filter((x) => x !== m) : [...a, m]));

  return (
    <Sheet
      open
      onOpenChange={(o) => !o && onClose()}
      title={request.mode === 'new' ? t('Ny aktivitet', 'New activity') : t('Redigera aktivitet', 'Edit activity')}
      wide
      footer={
        <>
          <Button onClick={onClose}>{t('Avbryt', 'Cancel')}</Button>
          <Button variant="primary" onClick={() => void save()} disabled={saving || !title.trim() || !!dstNote?.gap}>
            {saving ? t('Sparar…', 'Saving…') : t('Spara', 'Save')}
          </Button>
        </>
      }
    >
      <form
        className="grid gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        {existing?.recurrence && occKey && (
          <fieldset className="rounded-lg border border-border p-3">
            <legend className="px-1 text-sm font-medium">{t('Ändringen gäller', 'The change applies to')}</legend>
            <div className="flex flex-wrap gap-4">
              {(['occurrence', 'series'] as const).map((s) => (
                <label key={s} className="flex min-h-11 items-center gap-2 text-sm">
                  <input type="radio" name={`${uid}-scope`} checked={scope === s} onChange={() => setScope(s)} className="h-5 w-5 accent-[var(--color-accent)]" />
                  {s === 'occurrence' ? t('Bara denna förekomst', 'Only this occurrence') : t('Hela serien', 'Whole series')}
                </label>
              ))}
            </div>
          </fieldset>
        )}
        <Field label={t('Titel', 'Title')} htmlFor={`${uid}-title`}>
          <input id={`${uid}-title`} className={inputCls} value={title} onChange={(e) => setTitle(e.target.value)} required maxLength={300} autoFocus placeholder={t('t.ex. Sjukhusbesök', 'e.g. Hospital visit')} />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('Kalender', 'Calendar')} htmlFor={`${uid}-cal`}>
            <select id={`${uid}-cal`} className={inputCls} value={calendarId} onChange={(e) => setCalendarId(e.target.value)}>
              {writable.map((c) => (
                <option key={c.id} value={c.id}>
                  {calendarName(c)}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('Kategori', 'Category')} htmlFor={`${uid}-cat`}>
            <select id={`${uid}-cat`} className={inputCls} value={categoryId} onChange={(e) => onCategory(e.target.value)}>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {categoryName(c)}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <fieldset>
          <legend className="mb-1.5 text-sm font-medium">{t('Symbol', 'Symbol')}</legend>
          <div role="radiogroup" aria-label={t('Symbol', 'Symbol')} className="flex flex-wrap gap-1.5">
            {(Object.keys(ICONS) as IconId[]).map((id) => {
              const C = ICONS[id];
              return (
                <button
                  type="button"
                  key={id}
                  role="radio"
                  aria-checked={icon === id}
                  aria-label={iconLabel(id)}
                  title={iconLabel(id)}
                  onClick={() => setIcon(id)}
                  className={`grid h-11 w-11 place-items-center rounded-lg border ${icon === id ? 'border-accent bg-raised text-accent' : 'border-border text-muted hover:text-text'}`}
                >
                  <C size={20} aria-hidden />
                </button>
              );
            })}
          </div>
          <p className="mt-1 text-xs text-muted">
            {t('Vald:', 'Selected:')} {iconLabel(icon)}
          </p>
        </fieldset>

        <label className="flex min-h-11 items-center gap-2 text-sm">
          <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} className="h-5 w-5 accent-[var(--color-accent)]" />
          {t('Heldag', 'All day')}
        </label>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('Startdatum', 'Start date')} htmlFor={`${uid}-date`}>
            <input id={`${uid}-date`} type="date" className={inputCls} value={date} onChange={(e) => setDate(e.target.value)} required />
          </Field>
          <Field label={allDay ? t('Slutdatum (sista dagen)', 'End date (last day)') : t('Slutdatum', 'End date')} htmlFor={`${uid}-edate`}>
            <input id={`${uid}-edate`} type="date" className={inputCls} value={endDate} min={date} onChange={(e) => setEndDate(e.target.value)} />
          </Field>
        </div>
        {!allDay && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <Field label={t('Starttid (24 h)', 'Start time (24 h)')} htmlFor={`${uid}-start`}>
                <input id={`${uid}-start`} type="time" step={300} className={inputCls} value={start} onChange={(e) => setStart(e.target.value)} required />
              </Field>
              <Field label={t('Sluttid', 'End time')} htmlFor={`${uid}-end`} hint={end <= start && endDate === date ? t('Slutar nästa dag (t.ex. nattpass).', 'Ends the next day (e.g. a night shift).') : undefined}>
                <input id={`${uid}-end`} type="time" step={300} className={inputCls} value={end} onChange={(e) => setEnd(e.target.value)} required />
              </Field>
            </div>
            {dstNote && (
              <p className={`text-sm ${dstNote.gap ? 'text-risk' : 'text-warn'}`} role="alert">
                {dstNote.text}
              </p>
            )}
            <details className="rounded-lg border border-border px-3 py-2 text-sm">
              <summary className="min-h-9 cursor-pointer py-1 text-muted">{t('Tidszon och sommartid', 'Time zone and daylight saving')}</summary>
              <p className="mt-1 text-muted">
                {t('Tidszon:', 'Time zone:')} {timeZone}
              </p>
              <div className="mt-2 flex flex-col gap-1">
                <label className="flex min-h-11 items-center gap-2">
                  <input type="radio" checked={policy === 'wall'} onChange={() => setPolicy('wall')} className="h-5 w-5 accent-[var(--color-accent)]" />
                  {t('Behåll klockslagen vid flytt (08–16 förblir 08–16)', 'Keep the clock times when moving (08–16 stays 08–16)')}
                </label>
                <label className="flex min-h-11 items-center gap-2">
                  <input type="radio" checked={policy === 'elapsed'} onChange={() => setPolicy('elapsed')} className="h-5 w-5 accent-[var(--color-accent)]" />
                  {t('Behåll exakt längd (t.ex. 8 h även natten när sommartid ändras)', 'Keep the exact length (e.g. 8 h even on the night daylight saving changes)')}
                </label>
              </div>
            </details>
          </>
        )}

        <fieldset>
          <legend className="mb-1.5 text-sm font-medium">{t('Påminnelser', 'Reminders')}</legend>
          <div className="flex flex-wrap gap-1.5">
            {ALARM_PRESETS.map((p) => (
              <label key={p.minutes} className={`flex min-h-11 cursor-pointer items-center gap-2 rounded-full border px-3 text-sm ${alarms.includes(p.minutes) ? 'border-accent bg-raised' : 'border-border-strong text-muted'}`}>
                <input type="checkbox" className="h-4 w-4 accent-[var(--color-accent)]" checked={alarms.includes(p.minutes)} onChange={() => toggleAlarm(p.minutes)} />
                {presetLabel(p, allDay)}
              </label>
            ))}
          </div>
        </fieldset>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('Upprepa', 'Repeat')} htmlFor={`${uid}-rec`}>
            <select id={`${uid}-rec`} className={inputCls} value={recur} onChange={(e) => setRecur(e.target.value as RecurChoice)}>
              <option value="none">{t('Upprepas inte', 'Does not repeat')}</option>
              <option value="daily">{t('Dagligen', 'Daily')}</option>
              <option value="weekly">{t('Varje vecka', 'Every week')}</option>
              <option value="biweekly">{t('Varannan vecka', 'Every other week')}</option>
              <option value="monthly">{t('Varje månad (samma datum)', 'Every month (same date)')}</option>
              <option value="yearly">{t('Varje år', 'Every year')}</option>
            </select>
          </Field>
          {recur !== 'none' && (
            <Field label={t('Slutar', 'Ends')} htmlFor={`${uid}-rend`}>
              <div className="flex gap-2">
                <select id={`${uid}-rend`} className={inputCls} value={endMode} onChange={(e) => setEndMode(e.target.value as typeof endMode)}>
                  <option value="never">{t('Aldrig', 'Never')}</option>
                  <option value="until">{t('Datum', 'On date')}</option>
                  <option value="count">{t('Antal gånger', 'After a number of times')}</option>
                </select>
                {endMode === 'until' && <input aria-label={t('Slutdatum för serien', 'End date of the series')} type="date" className={inputCls} value={until} min={date} onChange={(e) => setUntil(e.target.value)} />}
                {endMode === 'count' && <input aria-label={t('Antal förekomster', 'Number of occurrences')} type="number" min={1} max={1000} className={inputCls} value={count} onChange={(e) => setCount(Number(e.target.value))} />}
              </div>
            </Field>
          )}
        </div>
        {(recur === 'weekly' || recur === 'biweekly') && (
          <fieldset>
            <legend className="mb-1.5 text-sm font-medium">{t('Veckodagar', 'Weekdays')}</legend>
            <div className="flex flex-wrap gap-1.5">
              {[1, 2, 3, 4, 5, 6, 7].map((i) => {
                const wd = i as Weekday;
                const short = weekdayName(wd, true);
                const d = short.charAt(0).toUpperCase() + short.slice(1);
                const on = weekdays.includes(wd);
                return (
                  <button type="button" key={d} aria-pressed={on} onClick={() => setWeekdays((w) => (on ? (w.length > 1 ? w.filter((x) => x !== wd) : w) : [...w, wd].sort()))} className={`min-h-11 min-w-12 rounded-lg border px-2 text-sm ${on ? 'border-accent bg-raised text-text' : 'border-border-strong text-muted'}`}>
                    {d}
                  </button>
                );
              })}
            </div>
          </fieldset>
        )}
        {recur === 'monthly' && Number(date.slice(8)) > 28 && (
          <p className="text-sm text-muted">
            {t(`Månader utan den ${Number(date.slice(8))}:e hoppas över – aktiviteten flyttas aldrig till den 1:a i nästa månad.`, `Months without day ${Number(date.slice(8))} are skipped – the activity is never moved to the 1st of the next month.`)}
          </p>
        )}

        <Field label={t('Plats', 'Location')} htmlFor={`${uid}-loc`}>
          <input id={`${uid}-loc`} className={inputCls} value={location} onChange={(e) => setLocation(e.target.value)} maxLength={500} />
        </Field>
        <Field label={t('Anteckningar', 'Notes')} htmlFor={`${uid}-notes`}>
          <textarea id={`${uid}-notes`} className={`${inputCls} min-h-24 py-2`} value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={20000} />
        </Field>
        <Field label={t('Länk (valfri)', 'Link (optional)')} htmlFor={`${uid}-url`} hint={t('Endast https:// eller http://. Öppnas bara när du klickar.', 'Only https:// or http://. Opens only when you click.')}>
          <input id={`${uid}-url`} type="url" inputMode="url" className={inputCls} value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://" />
        </Field>
        <label className="flex min-h-11 items-center gap-2 text-sm">
          <input type="checkbox" checked={priority === 'high'} onChange={(e) => setPriority(e.target.checked ? 'high' : 'normal')} className="h-5 w-5 accent-[var(--color-accent)]" />
          {t('Visa under Viktigt (personlig prioritet – inget juridiskt krav)', 'Show under Deadlines (personal priority – not a legal requirement)')}
        </label>
        {error && (
          <p className="rounded-lg border border-risk/60 bg-risk-bg px-3 py-2 text-sm" role="alert">
            {error}
          </p>
        )}
        {/* Dold standardknapp så att Enter i ett fält skickar formuläret. */}
        <button type="submit" hidden tabIndex={-1} />
      </form>
    </Sheet>
  );
}
